import { cloneDeep, merge } from "lodash";
import reduceReducers from "reduce-reducers";

import { ECONTRACT } from "constants/actionTypes";

import {
  apiModelOfflineCreateReducer,
  apiModelOfflineFetchReducer,
} from "lib/reducers";
import { deserializeEContract } from "lib/serializers/eContracts";

const create = apiModelOfflineCreateReducer(ECONTRACT, {
  deserializer: deserializeEContract,
});

const fetch = apiModelOfflineFetchReducer(ECONTRACT, {
  deserializer: deserializeEContract,
  clearOnRequest: true,
});

/**
 * while similar to apiModelOfflineUpdateReducer,
 * there are a couple of differences that prevent using directly:
 *  - subActionVerb used (VOID / EMAIL vs UPDATE)
 *  - REQUEST payload differences - only id is sent to these endpoints.
 *  - handling of request/success is subtly different
 *    - don't pre-emptively show any changes to the local instance
 *      until the server has responded with the updated payload
 */
function eContractVoidReducers(state, action) {
  switch (action.type) {
    case ECONTRACT.EMAIL.REQUEST:
    case ECONTRACT.VOID.REQUEST: {
      const { id } = action.payload;
      const data: EContract = deserializeEContract(
        action.payload,
        state.byId[id] || {},
      );
      return {
        ...state,
        byId: {
          ...state.byId,
          [id]: merge(cloneDeep(state.byId[id]), {
            ...data,
            syncing: true,
          }),
        },
      };
    }

    case ECONTRACT.EMAIL.SUCCESS:
    case ECONTRACT.VOID.SUCCESS: {
      const { payload } = action;
      const data: EContract = deserializeEContract(payload);

      return {
        ...state,
        byId: {
          ...state.byId,
          [data.id]: data,
        },
      };
    }

    default:
      return state;
  }
}

/**
 * Handle the ECONTRACT.GENERATE.SUCCESS payload, similar to,
 * and based on CREATE_BULK.SUCCESS case in apiModelOfflineCreateReducer
 * with some differences:
 *   - no payload with this request
 *   - wait for any new eContracts to be generated server side.
 *   - nothing to remove from state on error.
 */
function eContractGenerateReducers(state, action) {
  switch (action.type) {
    case ECONTRACT.GENERATE.SUCCESS: {
      const { payload } = action;
      const newState = { ...state.byId };

      const data: EContract[] = payload.map(p => deserializeEContract(p));

      const createdData = data.reduce(
        (acc: Record<string, EContract>, cur: EContract) => {
          acc[cur.id] = { ...cur };
          return acc;
        },
        {},
      );

      return {
        ...state,
        byId: {
          ...newState,
          ...createdData,
        },
      };
    }

    default:
      return state;
  }
}

const EContracts = reduceReducers(
  create,
  fetch,
  eContractVoidReducers,
  eContractGenerateReducers,
);

export default EContracts;
